var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var _videoEl, _src, _corePlayer, _Id, _logger, _videoInfo, _updateStatisticsInfoTimer, __status, _unbindEvent, _opts, _loadTimer, _eventBus, _handleFirstFrameTimeout, _initVideoInfo;
import { fMp4Player, Hls7Player, NativePlayer, BufferDetector } from '@bilibili-live/fmp4.js';
import { any2Str, CorePlayerType, EventBus, logger, remainBufferLength, roundNumber, mediaAction } from '@bilibili-live/web-player-common';
import { parseAudioChannel, redirectHLSV3 } from './video-utils';
// CorePlayer特殊说明：
//   // 从指定时间开始播放（轮播）
//   startTime: number
//   // network fetch(flv m3u8 m4s...) timeout, 单位ms
//   timeout: number
//   // 在buffer中保留n秒已使用的视频流
//   keepUsedBuffer: number
//   // ！！需要在MediaPlayer.src之前注册
//   createFetcher: Function
export var EventType;
(function (EventType) {
    /**
     * 1s触发一次，参数 VideoInfo
     */
    EventType["VideoInfo"] = "VideoInfo";
    EventType["MetaData"] = "MetaData";
    /**
     * 内核创建完成，事件回调参数({ corePlayer, videoEl, src })
     */
    EventType["CorePlayerCreated"] = "CorePlayerCreated";
    /**
     * 回调参数 { code, errInfo: { url, info } }
     */
    EventType["Error"] = "Error";
    /**
     * 开始加载流数据
     */
    EventType["LoadStart"] = "LoadStart";
    EventType["Ended"] = "Ended";
    /**
     * 能获取到拉流的response信息（如header）
     */
    EventType["NetworkResponse"] = "NetworkResponse";
    /**
     * 首包（流数据）
     */
    EventType["FirstPacket"] = "FirstPacket";
    /**
     * 视频首帧，收到该事件时，视频开始播放
     */
    EventType["FirstFrame"] = "FirstFrame";
    /**
     * 流中解析出SEI数据
     */
    EventType["SEIData"] = "SEIData";
    EventType["Destroyed"] = "Destroyed";
    /**
     * 卡顿开始
     */
    EventType["WaitStart"] = "WaitStart";
    /**
     * 卡顿结束
     */
    EventType["WaitEnd"] = "WaitEnd";
    /**
     * 卡顿上报，每一分钟触发一次，beforeunload时补一次
     */
    EventType["WaitReport"] = "WaitReport";
    /**
     * 开始播放事件，调用了 play(), videoEl.paused由 false 变为 true（注意与 plaing 事件的区别）
     */
    EventType["Play"] = "Play";
    /**
     * 播放器暂停事件，调用了 pause()
     */
    EventType["Pause"] = "Pause";
    /**
     * WASM decoder 需要降级，性能扛不住时触发
     */
    EventType["WASMDecoderDegrade"] = "WASMDecoderDegrade";
    /**
     * 不能带声音自动播放
     */
    EventType["MutePlay"] = "MutePlay";
    /**
     * 不完自动播放
     */
    EventType["NotAutoPlay"] = "NotAutoPlay";
    /**
     * 首帧触发超时
     */
    EventType["FirstFrameTimeout"] = "FirstFrameTimeout";
})(EventType || (EventType = {}));
export var VideoStatus;
(function (VideoStatus) {
    VideoStatus["Initialize"] = "Initialize";
    VideoStatus["Loading"] = "Loading";
    VideoStatus["Playing"] = "Playing";
    VideoStatus["Pause"] = "Pause";
    VideoStatus["Destroyed"] = "Destroyed";
})(VideoStatus || (VideoStatus = {}));
// 用于区分日志
let CORE_INSTCANCE_COUNT = 0;
const defRealtimeInfo = {
    droppedFrames: 0,
    decodedFrames: 0,
    videoBufferLength: 0,
    videoNetworkActivity: 0,
    audioNetworkActivity: 0,
    audioBufferLength: 0
};
const defMediaInfo = {
    corePlayerType: CorePlayerType.NativePlayer,
    width: 0,
    height: 0,
    mimeType: '',
    volume: 0,
    fps: 0,
    encoder: '',
    videoDataRate: 0,
    audioDataRate: 0,
    audioSampleRate: 0,
    audioChannel: '',
    videoSrc: ''
};
export var VideoError;
(function (VideoError) {
    VideoError[VideoError["FirstFrameTimeout"] = 92001] = "FirstFrameTimeout";
})(VideoError || (VideoError = {}));
export class Video {
    constructor(src, opts = {}) {
        var _a;
        // bwp-video 包含 destroy 方法
        _videoEl.set(this, void 0);
        _src.set(this, void 0);
        _corePlayer.set(this, null);
        _Id.set(this, void 0);
        _logger.set(this, void 0);
        /**
         * 当前播放的视频元信息，及统计信息
         */
        _videoInfo.set(this, {
            mediaInfo: Object.assign({}, defMediaInfo),
            realtimeInfo: Object.assign({}, defRealtimeInfo)
        });
        _updateStatisticsInfoTimer.set(this, null);
        __status.set(this, VideoStatus.Initialize
        /**
         * 取消video节点上绑定的事件
         */
        );
        /**
         * 取消video节点上绑定的事件
         */
        _unbindEvent.set(this, []);
        _opts.set(this, void 0);
        /**
         * load的定时器，默认1ms后自动load
         */
        _loadTimer.set(this, void 0);
        _eventBus.set(this, new EventBus());
        this.on = __classPrivateFieldGet(this, _eventBus).on;
        this.once = __classPrivateFieldGet(this, _eventBus).once;
        this.emit = (type, ...args) => {
            if (![EventType.VideoInfo, EventType.SEIData].includes(type)) {
                __classPrivateFieldGet(this, _logger).debug(`video emit: ${type}, handlers count: ${__classPrivateFieldGet(this, _eventBus).getHandlers(type).size}`);
            }
            __classPrivateFieldGet(this, _eventBus).emit(type, ...args);
        };
        /**
         * 超过十秒未首帧则为超时,抛出错误
         * 外部应该处理这个错误，重试或者提示
         */
        _handleFirstFrameTimeout.set(this, () => {
            const firstFrameTimeoutTimer = setTimeout(() => {
                this.emit(EventType.Error, {
                    type: EventType.FirstFrameTimeout,
                    code: VideoError.FirstFrameTimeout,
                    errInfo: { url: __classPrivateFieldGet(this, _src) }
                });
            }, 10 * 1000);
            const clear = () => {
                clearTimeout(firstFrameTimeoutTimer);
            };
            this.once(EventType.FirstFrame, clear);
            return () => {
                clear();
            };
        });
        _initVideoInfo.set(this, (metaData) => {
            var _a, _b;
            __classPrivateFieldGet(this, _logger).info('metadata fired');
            __classPrivateFieldGet(this, _videoInfo).mediaInfo = Object.assign({}, __classPrivateFieldGet(this, _videoInfo).mediaInfo, {
                videoSrc: __classPrivateFieldGet(this, _src),
                width: metaData.width,
                height: metaData.height,
                mimeType: (_a = __classPrivateFieldGet(this, _corePlayer)) === null || _a === void 0 ? void 0 : _a.mimeType,
                fps: roundNumber(metaData.fps, 0),
                encoder: (_b = metaData.encoder) !== null && _b !== void 0 ? _b : metaData.creator,
                videoDataRate: metaData.videodatarate,
                audioDataRate: metaData.audiodatarate,
                audioSampleRate: metaData.audiosamplerate,
                audioChannel: parseAudioChannel(metaData.audiochannel)
            });
            this.emit(EventType.VideoInfo, this.getVideoInfo());
            this.updateStatisticsInfo();
        });
        __classPrivateFieldSet(this, _Id, CORE_INSTCANCE_COUNT);
        CORE_INSTCANCE_COUNT += 1;
        // 代理logger，给所有日志加上corePlayerId，用于区分实例（可能）
        __classPrivateFieldSet(this, _logger, new Proxy(logger, {
            get: (obj, prop) => {
                if (['debug', 'info', 'warn', 'error'].includes(prop)) {
                    return (log) => {
                        obj[prop](`id ${__classPrivateFieldGet(this, _Id)}: ${log}`);
                    };
                }
                return obj[prop];
            }
        }));
        __classPrivateFieldSet(this, _src, src);
        __classPrivateFieldSet(this, _opts, opts);
        __classPrivateFieldSet(this, _videoEl, createVideoEl((_a = opts.wasmDecode) !== null && _a !== void 0 ? _a : false)
        // 延迟load，让调用方有时间注册事件
        );
        // 延迟load，让调用方有时间注册事件
        __classPrivateFieldSet(this, _loadTimer, window.setTimeout(() => {
            this.load();
        }, 1));
    }
    get status() {
        return __classPrivateFieldGet(this, __status);
    }
    set status(status) {
        __classPrivateFieldGet(this, _logger).info(`Video status changed to ${status}`);
        __classPrivateFieldSet(this, __status, status);
    }
    /**
     * 终止自动load，调用方控制load时机（必须调用）
     */
    delyLoad() {
        clearTimeout(__classPrivateFieldGet(this, _loadTimer));
    }
    /**
     * 加载流，即将开始播放视频
     * @param src 要播放的流地址
     * @param opts startTime：从流的指定时间开始播放, 单位‘秒’（仅用于支持轮播）
     */
    load() {
        var _a;
        if (this.status === VideoStatus.Destroyed)
            return;
        __classPrivateFieldGet(this, _logger).info(`load src: ${__classPrivateFieldGet(this, _src)}, opts: ${JSON.stringify(__classPrivateFieldGet(this, _opts))}`);
        this.status = VideoStatus.Loading;
        this.createCorePlayer(__classPrivateFieldGet(this, _videoEl), (_a = __classPrivateFieldGet(this, _opts)) === null || _a === void 0 ? void 0 : _a.startTime);
        this.play().catch(err => {
            logger.error(err);
        });
    }
    play() {
        return __awaiter(this, void 0, void 0, function* () {
            __classPrivateFieldGet(this, _logger).info('video play');
            if (__classPrivateFieldGet(this, _videoEl) == null)
                return;
            if (this.status === VideoStatus.Playing)
                return;
            // safari禁用带声音自动播放，AbortError: The operation was aborted.
            // safari永不自动播放，NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.
            // chrome 用户未交互时默认禁止播放， NotAllowedError, play() failed because the user didn't interact with the document first
            try {
                yield __classPrivateFieldGet(this, _videoEl).play();
                this.status = VideoStatus.Playing;
            }
            catch (err) {
                // 播放器出现其他错误也会走进此处，需退出
                if (err.name !== 'NotAllowedError')
                    return;
                __classPrivateFieldGet(this, _logger).warn('try mute play');
                __classPrivateFieldGet(this, _videoEl).muted = true;
                try {
                    yield __classPrivateFieldGet(this, _videoEl).play();
                    this.status = VideoStatus.Playing;
                    this.emit(EventType.MutePlay);
                }
                catch (err) {
                    __classPrivateFieldGet(this, _logger).warn('can\'t auto play');
                    this.status = VideoStatus.Pause;
                    this.emit(EventType.NotAutoPlay);
                }
            }
        });
    }
    pause() {
        __classPrivateFieldGet(this, _logger).info('video pause');
        if (__classPrivateFieldGet(this, _videoEl) == null || this.status !== VideoStatus.Playing)
            return;
        __classPrivateFieldGet(this, _videoEl).pause();
        this.status = VideoStatus.Pause;
    }
    /**
     * 支持两种方式向前跳，单位s
     * 如果不能满足期望（剩余buffer 5s，期望forward 6s），则忽略本次操作
     * 优先使用keepBuffer
     * @param param forward: 向前跳n秒； keepBuffer：保留n秒的buffer
     */
    seek({ forward, keepBuffer }) {
        __classPrivateFieldGet(this, _logger).info(`video seek, forward: ${forward !== null && forward !== void 0 ? forward : 'null'}, keepBuffer: ${keepBuffer !== null && keepBuffer !== void 0 ? keepBuffer : 'null'}`);
        if (__classPrivateFieldGet(this, _videoEl) == null)
            return;
        const bufLen = remainBufferLength(__classPrivateFieldGet(this, _videoEl));
        if (bufLen <= 0)
            return;
        if (keepBuffer != null && keepBuffer > 0) {
            if (bufLen < keepBuffer)
                return;
            __classPrivateFieldGet(this, _videoEl).currentTime += bufLen - keepBuffer;
        }
        else if (forward != null && forward > 0) {
            if (forward > bufLen)
                return;
            __classPrivateFieldGet(this, _videoEl).currentTime += forward;
        }
    }
    getSrc() {
        return __classPrivateFieldGet(this, _src);
    }
    /**
     * 设置视频音量
     * @param v 音量值[0, 1], 参考 HTMLVideoElement.property.volume
     */
    volume(v) {
        __classPrivateFieldGet(this, _logger).info(`video setVolume(${v})`);
        if (__classPrivateFieldGet(this, _videoEl) == null)
            return;
        __classPrivateFieldGet(this, _videoEl).volume = roundNumber(v, 2);
    }
    /**
     * 视频静音
     */
    mute(v) {
        if (__classPrivateFieldGet(this, _videoEl) == null)
            return;
        __classPrivateFieldGet(this, _videoEl).muted = v;
    }
    getVideoEl() {
        return __classPrivateFieldGet(this, _videoEl);
    }
    removeVideoEl() {
        var _a, _b;
        __classPrivateFieldGet(this, _videoEl).remove();
        // bwp-video (软解 HEVC) 需要主动 destroy
        (_b = (_a = __classPrivateFieldGet(this, _videoEl)).destroy) === null || _b === void 0 ? void 0 : _b.call(_a);
    }
    getVideoInfo() {
        __classPrivateFieldGet(this, _videoInfo).mediaInfo.volume = __classPrivateFieldGet(this, _videoEl).volume;
        return __classPrivateFieldGet(this, _videoInfo);
    }
    /**
     * 捕获当前的画面，返回 DataURL
     */
    capturePic(imgType, quality) {
        var _a, _b;
        // 如果是 bwp-video(hevc 软解)
        const dataURL = (_b = (_a = __classPrivateFieldGet(this, _videoEl)).toDataURL) === null || _b === void 0 ? void 0 : _b.call(_a, imgType, quality);
        if (dataURL != null)
            return dataURL;
        // 不同 video
        return capturePicFromVideo(__classPrivateFieldGet(this, _videoEl), imgType, quality);
    }
    /**
     * 销毁Video实例，与Player.destroy保持一致
     * @param holdLastFrame 是否保留最后一个画面，默认 false
     */
    destroy(holdLastFrame = false) {
        var _a, _b;
        // 某些场景先断流 holdLastFrame: true，后再次销毁 holdLastFrame: false
        // 避免 video 标签保留在 DOM，宁可多remove几次
        if (!holdLastFrame)
            this.removeVideoEl();
        if (this.status === VideoStatus.Destroyed)
            return;
        this.status = VideoStatus.Destroyed;
        __classPrivateFieldGet(this, _logger).info(`video destroyed, holdLastFrame: ${holdLastFrame.toString()}`);
        clearTimeout(__classPrivateFieldGet(this, _loadTimer));
        clearInterval((_a = __classPrivateFieldGet(this, _updateStatisticsInfoTimer)) !== null && _a !== void 0 ? _a : 0);
        __classPrivateFieldSet(this, _videoInfo, { mediaInfo: Object.assign({}, defMediaInfo), realtimeInfo: Object.assign({}, defRealtimeInfo) }
        // 暂停video，让 UI 更新播放状态
        );
        // 暂停video，让 UI 更新播放状态
        __classPrivateFieldGet(this, _videoEl).pause();
        (_b = __classPrivateFieldGet(this, _corePlayer)) === null || _b === void 0 ? void 0 : _b.destroy();
        __classPrivateFieldGet(this, _unbindEvent).forEach(fn => fn === null || fn === void 0 ? void 0 : fn());
        this.emit(EventType.Destroyed);
        __classPrivateFieldGet(this, _eventBus).destroy();
    }
    createCorePlayer(videoEl, startTime) {
        if (__classPrivateFieldGet(this, _src) == null)
            return;
        const mediaSource = __classPrivateFieldGet(this, _opts).wasmDecode === true
            ? new window.BwpMediaSource()
            : window.MediaSource != null
                ? new window.MediaSource()
                : {};
        const streamUrl = new URL(__classPrivateFieldGet(this, _src));
        let corePlayer;
        let corePlayerType;
        if (streamUrl.pathname.endsWith('.flv')) {
            // eslint-disable-next-line
            corePlayer = new fMp4Player(mediaSource, videoEl);
            corePlayerType = CorePlayerType.fMp4Player;
        }
        else if (streamUrl.pathname.endsWith('.m3u8')) {
            if (__classPrivateFieldGet(this, _opts).forceNativePlayer === true || window.MediaSource == null) {
                corePlayer = new NativePlayer(mediaSource, videoEl);
                corePlayerType = CorePlayerType.NativePlayer;
            }
            else {
                corePlayer = new Hls7Player(mediaSource, videoEl);
                corePlayerType = CorePlayerType.Hls7Player;
            }
        }
        else if (streamUrl.pathname.endsWith('.mp4')) {
            // 清晰读360P以下的清晰度的轮播只有mp4资源
            corePlayer = new NativePlayer(mediaSource, videoEl);
            corePlayerType = CorePlayerType.NativePlayer;
        }
        else {
            throw new Error(`Unsupported stream url: ${streamUrl.pathname}`);
        }
        __classPrivateFieldGet(this, _videoInfo).mediaInfo = Object.assign({}, __classPrivateFieldGet(this, _videoInfo).mediaInfo, {
            corePlayerType
        });
        this.emit(EventType.LoadStart, {
            corePlayer,
            videoEl,
            src: __classPrivateFieldGet(this, _src)
        });
        corePlayer.keepUsedBuffer = 10;
        corePlayer.timeout = 7000;
        __classPrivateFieldGet(this, _unbindEvent).push(this.bindEvents(corePlayer, videoEl), __classPrivateFieldGet(this, _handleFirstFrameTimeout).call(this));
        if (startTime != null &&
            startTime > 0 &&
            corePlayer instanceof fMp4Player)
            corePlayer.startTime = startTime;
        if (corePlayerType === CorePlayerType.NativePlayer) {
            redirectHLSV3(__classPrivateFieldGet(this, _src)).then(res => {
                corePlayer.src = res;
            }).catch(err => {
                corePlayer.src = __classPrivateFieldGet(this, _src);
                logger.error(err);
            });
        }
        else {
            corePlayer.src = __classPrivateFieldGet(this, _src);
        }
        __classPrivateFieldSet(this, _corePlayer, corePlayer);
        this.emit(EventType.CorePlayerCreated, {
            corePlayer,
            videoEl,
            src: __classPrivateFieldGet(this, _src)
        });
    }
    updateStatisticsInfo() {
        let lastReceiveBytes = 0;
        if (__classPrivateFieldGet(this, _updateStatisticsInfoTimer) != null) {
            clearInterval(__classPrivateFieldGet(this, _updateStatisticsInfoTimer));
        }
        // 实时信息每秒更新一次
        __classPrivateFieldSet(this, _updateStatisticsInfoTimer, window.setInterval(() => {
            var _a, _b, _c;
            const { droppedVideoFrames = 0, totalVideoFrames = 0 } = (_c = (_b = (_a = __classPrivateFieldGet(this, _videoEl)) === null || _a === void 0 ? void 0 : _a.getVideoPlaybackQuality) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : {};
            let bytes = 0;
            if (__classPrivateFieldGet(this, _corePlayer) != null) {
                bytes = __classPrivateFieldGet(this, _corePlayer).receivedBytes - lastReceiveBytes;
                if (Number.isNaN(bytes))
                    bytes = 0;
                lastReceiveBytes = __classPrivateFieldGet(this, _corePlayer).receivedBytes;
            }
            __classPrivateFieldGet(this, _videoInfo).realtimeInfo = Object.assign({}, __classPrivateFieldGet(this, _videoInfo).realtimeInfo, {
                droppedFrames: droppedVideoFrames,
                decodedFrames: totalVideoFrames,
                videoBufferLength: __classPrivateFieldGet(this, _videoEl) == null ? 0 : roundNumber(remainBufferLength(__classPrivateFieldGet(this, _videoEl)), 3),
                videoNetworkActivity: bytes
            });
            this.emit(EventType.VideoInfo, this.getVideoInfo());
        }, 1000));
    }
    /**
     * 监听并触发事件，注意不要些太长的逻辑（一个事件超过5行请抽离到外部）
     * @param mediaPlayer 内核
     * @param videoEl video element
     */
    bindEvents(mediaPlayer, videoEl) {
        const startTs = performance.now();
        // 标识错误或流正常结束
        let ended = false;
        mediaPlayer.onMetaData = (metaData) => {
            __classPrivateFieldGet(this, _initVideoInfo).call(this, metaData);
            this.emit(EventType.MetaData, performance.now() - startTs);
            __classPrivateFieldGet(this, _logger).info(`after onMetaData mediaInfo: ${JSON.stringify(__classPrivateFieldGet(this, _videoInfo).mediaInfo)}`);
        };
        mediaPlayer.onSeiData = (...args) => {
            this.emit(EventType.SEIData, ...args);
        };
        mediaPlayer.onFileFetched = (url, { used, duration }) => {
            // used：下载所用时长，duration：视频持续时长（m3u8、首个m4s duration为0）
            let threshold = 2000;
            if (duration === 0)
                threshold = 2000;
            else if (duration <= 500)
                threshold = 500; // 有时duration可能会很小
            if (used > threshold) {
                __classPrivateFieldGet(this, _logger).warn(`fetch slowly: ${url}, cost ${used}ms, duration: ${duration}ms`);
            }
        };
        mediaPlayer.onFirstResponse = (res) => {
            this.emit(EventType.NetworkResponse, res);
        };
        mediaPlayer.onEndOfStream = () => {
            // video 标签的ended可能不会触发，需要手动dispatch
            // bufLen可能是负数、NaN
            let bufLen = Math.ceil(remainBufferLength(videoEl));
            if (bufLen < 0 || isNaN(bufLen))
                bufLen = 0;
            __classPrivateFieldGet(this, _logger).warn(`video onEndOfStream, bufLen: ${bufLen}`);
            setTimeout(() => {
                videoEl.dispatchEvent(new Event('ended'));
            }, bufLen * 1000);
        };
        mediaPlayer.onFirstPacket = () => {
            const time = performance.now() - startTs;
            __classPrivateFieldGet(this, _logger).info(`video first packet cost: ${Math.round(time)}`);
            this.emit(EventType.FirstPacket, performance.now() - startTs);
        };
        const onPlayerCoreError = (type, code, errInfo) => {
            if (ended)
                return;
            ended = true;
            // code 取值含义（fmp4.js error-code.js）
            const msg = any2Str({ code, errInfo });
            __classPrivateFieldGet(this, _logger).error(`player core ${type}, ${msg}`);
            this.emit(EventType.Error, { code, errInfo });
            this.destroy();
        };
        mediaPlayer.onNetworkError = onPlayerCoreError.bind(this, 'NetworkError');
        mediaPlayer.onParserError = onPlayerCoreError.bind(this, 'ParserError');
        const onLoadeddata = () => {
            const time = performance.now() - startTs;
            __classPrivateFieldGet(this, _logger).info(`video first frame cost: ${Math.round(time)}`);
            this.emit(EventType.FirstFrame, time);
        };
        videoEl.addEventListener('loadeddata', onLoadeddata);
        const onEnded = () => {
            if (ended)
                return;
            ended = true;
            this.emit(EventType.Ended);
            this.destroy();
        };
        videoEl.addEventListener('ended', onEnded);
        const onNativeError = () => {
            if (ended)
                return;
            ended = true;
            const error = videoEl.error;
            const info = {
                code: 90002,
                errInfo: {
                    code: error === null || error === void 0 ? void 0 : error.code,
                    msg: error === null || error === void 0 ? void 0 : error.message
                }
            };
            __classPrivateFieldGet(this, _logger).error(`video native error: ${any2Str(info)}`);
            this.emit(EventType.Error, info);
            this.destroy();
        };
        videoEl.addEventListener('error', onNativeError);
        const stopDetect = detectWaiting(videoEl, mediaPlayer, {
            startCb: () => {
                __classPrivateFieldGet(this, _logger).warn(`video waiting!, bufferLen: ${remainBufferLength(videoEl)}`);
                this.emit(EventType.WaitStart);
            },
            endCb: () => {
                __classPrivateFieldGet(this, _logger).info('video playing');
                this.emit(EventType.WaitEnd);
            },
            reportCb: (data) => {
                this.emit(EventType.WaitReport, data);
            }
        });
        const onPlay = () => {
            this.status = VideoStatus.Playing;
            this.emit(EventType.Play);
        };
        videoEl.addEventListener('play', onPlay);
        const onPause = () => {
            this.status = VideoStatus.Pause;
            this.emit(EventType.Pause);
        };
        videoEl.addEventListener('pause', onPause);
        let onWASMDecoderDegrade = () => { };
        if (__classPrivateFieldGet(this, _opts).wasmDecode === true) {
            onWASMDecoderDegrade = () => {
                this.emit(EventType.WASMDecoderDegrade);
            };
            videoEl.addEventListener('performance', onWASMDecoderDegrade);
        }
        const offPause = mediaAction.on('pause', () => {
            videoEl.pause();
        });
        const offPlay = mediaAction.on('play', () => {
            videoEl.play().catch(err => { logger.error(err); });
        });
        return () => {
            videoEl.removeEventListener('loadeddata', onLoadeddata);
            videoEl.removeEventListener('ended', onEnded);
            videoEl.removeEventListener('error', onNativeError);
            videoEl.removeEventListener('play', onPlay);
            videoEl.removeEventListener('pause', onPause);
            videoEl.removeEventListener('performance', onWASMDecoderDegrade);
            stopDetect();
            offPause();
            offPlay();
        };
    }
}
_videoEl = new WeakMap(), _src = new WeakMap(), _corePlayer = new WeakMap(), _Id = new WeakMap(), _logger = new WeakMap(), _videoInfo = new WeakMap(), _updateStatisticsInfoTimer = new WeakMap(), __status = new WeakMap(), _unbindEvent = new WeakMap(), _opts = new WeakMap(), _loadTimer = new WeakMap(), _eventBus = new WeakMap(), _handleFirstFrameTimeout = new WeakMap(), _initVideoInfo = new WeakMap();
function createVideoEl(wasmDecode) {
    const videoEl = document.createElement(wasmDecode ? 'bwp-video' : 'video');
    videoEl.id = `video-${Math.random().toString().slice(2)}`;
    videoEl.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    overflow: hidden;
  `;
    videoEl.volume = 0.9;
    videoEl.autoplay = true;
    return videoEl;
}
/**
 * 视频播放卡顿检测
 * @param videoEl HTMLVideoElement
 * @param startCb 卡顿开始回调
 * @param endCb 卡顿结束回调，duration为0表示不需要上报的卡顿
 */
function detectWaiting(videoEl, corePlayer, { startCb, endCb, reportCb }) {
    // 抛出播放器的卡顿、播放事件
    let isLoaded = false;
    const onLoadeddata = () => {
        isLoaded = true;
        videoEl.removeEventListener('loadeddata', onLoadeddata);
    };
    videoEl.addEventListener('loadeddata', onLoadeddata);
    let isWaiting = false;
    // safari 有几率不触发 playing 事件，需要progress事件结束 waiting
    const onProgress = () => {
        videoEl.removeEventListener('progress', onProgress);
        if (isWaiting) {
            isWaiting = false;
            endCb();
        }
    };
    const onWaiting = () => {
        // 若未触发首帧，忽略 waiting 事件
        if (!isLoaded)
            return;
        isWaiting = true;
        startCb();
        videoEl.addEventListener('progress', onProgress);
    };
    const onPlaying = () => {
        videoEl.removeEventListener('progress', onProgress);
        if (!isWaiting)
            return;
        isWaiting = false;
        endCb();
    };
    videoEl.addEventListener('waiting', onWaiting);
    videoEl.addEventListener('playing', onPlaying);
    // 当卡顿发生时（onwaiting），BufferDetector会暂停播放，等待buffer超过1s（最多等待2s），避免多次但短暂的卡顿
    const bufDetector = new BufferDetector(videoEl, 1000);
    let lastCount = 0;
    let lastDuration = 0;
    let lastPlayTime = 0;
    let beforeReceivedBytes = 0;
    let lastTime = performance.now();
    const report = () => {
        const curTime = performance.now();
        const receivedBytes = corePlayer.receivedBytes - beforeReceivedBytes;
        beforeReceivedBytes = corePlayer.receivedBytes;
        reportCb({
            count: bufDetector.waitedCount - lastCount,
            duration: Math.round(bufDetector.waitedDuration - lastDuration),
            windowSize: Math.round(curTime - lastTime),
            playTime: Math.round(videoEl.currentTime - lastPlayTime),
            playedTime: Math.round(videoEl.currentTime),
            receivedBytes
        });
        lastCount = bufDetector.waitedCount;
        lastDuration = bufDetector.waitedDuration;
        lastPlayTime = videoEl.currentTime;
        lastTime = curTime;
    };
    const timer = window.setInterval(report, 60 * 1000);
    const onBeforeunload = () => {
        report();
    };
    window.addEventListener('beforeunload', onBeforeunload);
    return () => {
        clearInterval(timer);
        // 销毁前需要补报已累积的值
        report();
        window.removeEventListener('beforeunload', onBeforeunload);
        videoEl.removeEventListener('playing', onPlaying);
        videoEl.removeEventListener('waiting', onWaiting);
        videoEl.removeEventListener('loadeddata', onLoadeddata);
        bufDetector.destroy();
    };
}
// 检查video 比例，竖屏需要创建背景
export function checkVideoRate(videoEl) {
    const vh = videoEl.videoHeight;
    const vw = videoEl.videoWidth;
    if (vh > vw) {
        const backgroundEl = createVideoBgEl(videoEl);
        videoEl.insertAdjacentElement('afterend', backgroundEl);
        return backgroundEl;
    }
    return null;
}
// 竖屏开播，截取背景图
function createVideoBgEl(video) {
    const image = capturePicFromVideo(video);
    let backgroundEl = null;
    backgroundEl = document.createElement('div');
    backgroundEl.style.cssText = `
    background-image: url(${image});
    background-size: cover;
    background-position: center;
    width: 100%;
    height: 100%;
    filter: blur(35px);
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  `;
    return backgroundEl;
}
/**
 * 从 Video 中截图
 * 默认：image/jpeg 0.8
 */
function capturePicFromVideo(videoEl, imgType = 'image/jpeg', quality = 0.8) {
    var _a;
    const c = document.createElement('canvas');
    c.width = videoEl.videoWidth;
    c.height = videoEl.videoHeight;
    (_a = c.getContext('2d')) === null || _a === void 0 ? void 0 : _a.drawImage(videoEl, 0, 0, c.width, c.height);
    return c.toDataURL(imgType, quality);
}
//# sourceMappingURL=video.js.map